/**
 * @fileoverview Local-first department authentication setup service.
 * @module system-web/services/departmentAuthService
 *
 * Responsibilities:
 * - Read and observe department authentication state from system Dexie.
 * - Send setup mutations through guarded APIs and reconcile canonical responses to Dexie.
 * - Own all online-only setup operations and never expose secret values to logs.
 *
 * Dependencies:
 * - System Dexie database, apiFetch, and shared telemetry.
 *
 * Architecture:
 * - Dexie is the UI source of truth; setup endpoints reconcile updated tenants.
 *
 * Notes:
 * - Client secrets are write-only and are not stored in synchronized tenant records.
 *
 * @localFirst
 * @security Secrets remain in API-owned collections and are never written to
 *            tenant sync records.
 * @logging Emits sanitized operation events only.
 */
import { getSystemDb } from '@firesquirrel/local-db';
import { liveQuery, type Observable } from 'dexie';
import { apiFetch } from '../apiClient';
import { telemetry } from './telemetry';

export type DepartmentAuthMethod = 'local' | 'microsoft365' | 'google' | 'saml' | 'externalIdp';
export type DepartmentAuthSetupStatus =
  | 'not_configured' | 'draft' | 'awaiting_it' | 'it_submitted'
  | 'ready_to_validate' | 'validation_failed' | 'validated' | 'active';
export type AuthSetupStage = 'choose' | 'jit' | 'packet' | 'credentials' | 'validate' | 'activate' | 'manage';

export interface DepartmentAuthState {
  tenant: any | null;
  authMethod: DepartmentAuthMethod;
  setupStatus: DepartmentAuthSetupStatus;
  provider: any;
  setupPackets: any[];
  active: boolean;
}

export interface DepartmentAuthDraftPatch {
  authMethod?: DepartmentAuthMethod;
  providerType?: string;
  name?: string;
  upstreamTenantId?: string;
  onMicrosoftDomain?: string;
  departmentItContact?: { name?: string; email?: string };
  defaultNonAdminRole?: string;
  jitProvisioning?: Record<string, unknown>;
  jit?: Record<string, unknown>;
  sessionPolicy?: Record<string, unknown>;
  federatedProvider?: Record<string, unknown>;
}

const normalizeState = (tenant: any): DepartmentAuthState => {
  const auth = tenant?.authenticationSettings ?? {};
  return {
    tenant,
    authMethod: auth.authMethod ?? 'local',
    setupStatus: auth.setupStatus ?? (auth.authMethod && auth.authMethod !== 'local' ? 'draft' : 'not_configured'),
    provider: auth.federatedProvider ?? null,
    setupPackets: Array.isArray(auth.setupPackets) ? auth.setupPackets : [],
    active: auth.authMethod !== 'local' && auth.setupStatus === 'active',
  };
};

export function deriveAuthSetupStage(state: Pick<DepartmentAuthState, 'authMethod' | 'setupStatus'>): AuthSetupStage {
  if (state.setupStatus === 'active') return 'manage';
  if (state.setupStatus === 'validated') return 'activate';
  if (state.setupStatus === 'ready_to_validate' || state.setupStatus === 'validation_failed') return 'validate';
  if (state.setupStatus === 'it_submitted') return 'credentials';
  if (state.setupStatus === 'awaiting_it') return 'packet';
  if (state.setupStatus === 'draft' || state.setupStatus === 'not_configured') return 'choose';
  return state.authMethod === 'local' ? 'choose' : 'jit';
}

export function watchDepartmentAuthState(tenantId: string): Observable<DepartmentAuthState> {
  return liveQuery(async () => {
    const db = await getSystemDb();
    return normalizeState(await db.tenants.get(tenantId));
  });
}

export async function getDepartmentAuthState(tenantId: string): Promise<DepartmentAuthState> {
  const db = await getSystemDb();
  return normalizeState(await db.tenants.get(tenantId));
}

async function reconcileTenant(response: any): Promise<any | null> {
  const tenant = response?.tenant ?? response;
  if (!tenant?._id) return null;
  const db = await getSystemDb();
  await db.tenants.put(tenant);
  return tenant;
}

async function reconcileResponse(response: any): Promise<any> {
  await reconcileTenant(response);
  return response;
}

function trackSetupEvent(operation: string, result: 'success' | 'failure'): void {
  telemetry.trackEvent('department_auth_setup', { operation, result });
}

export async function saveAuthSetupDraft(tenantId: string, draftPatch: DepartmentAuthDraftPatch): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/draft`, {
      method: 'PUT',
      body: JSON.stringify({
        providerType: draftPatch.providerType,
        name: draftPatch.name,
        upstreamTenantId: draftPatch.upstreamTenantId,
        onMicrosoftDomain: draftPatch.onMicrosoftDomain,
        departmentItContact: draftPatch.departmentItContact,
        jitProvisioning: draftPatch.jitProvisioning,
      }),
    });
    trackSetupEvent('draft_saved', 'success');
    return reconcileTenant(response);
  } catch (error) {
    trackSetupEvent('draft_saved', 'failure');
    throw error;
  }
}

export async function generateItSetupPacket(tenantId: string): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/packet`, { method: 'POST' });
    trackSetupEvent('packet_generated', 'success');
    return reconcileResponse(response);
  } catch (error) {
    trackSetupEvent('packet_generated', 'failure');
    throw error;
  }
}

export async function getItSetupPacket(tenantId: string, version: number): Promise<any> {
  return apiFetch(`/api/tenants/${tenantId}/auth-setup/packet/${version}`);
}

export async function createItHandoffLink(tenantId: string, options: { sendEmail: boolean }): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/handoff-token`, {
      method: 'POST',
      body: JSON.stringify({ send: options.sendEmail }),
    });
    trackSetupEvent(options.sendEmail ? 'handoff_email_sent' : 'handoff_link_created', 'success');
    return response;
  } catch (error) {
    trackSetupEvent(options.sendEmail ? 'handoff_email_sent' : 'handoff_link_created', 'failure');
    throw error;
  }
}

export async function submitItCredentialsAdmin(tenantId: string, credentials: Record<string, unknown>): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/credentials`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    trackSetupEvent('credentials_submitted', 'success');
    return reconcileTenant(response);
  } catch (error) {
    trackSetupEvent('credentials_submitted', 'failure');
    throw error;
  }
}

export async function runServerValidationAndGraphSync(tenantId: string): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/validate-server`, { method: 'POST' });
    await reconcileTenant(response);
    if (response?.ok === false) {
      throw new Error(`Entra credential validation failed (${response.code ?? 'SERVER_VALIDATION_FAILED'}). Fix the configuration and run validation again.`);
    }
    trackSetupEvent('server_validation', 'success');
    return response?.tenant ?? response;
  } catch (error) {
    trackSetupEvent('server_validation', 'failure');
    throw error;
  }
}

export async function createInteractiveTestSession(tenantId: string): Promise<any> {
  const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/test-session`, { method: 'POST' });
  await reconcileTenant(response);
  return response;
}

export async function activateLockedAuthMethod(tenantId: string): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/activate`, { method: 'POST' });
    trackSetupEvent('authentication_activated', 'success');
    return reconcileTenant(response);
  } catch (error) {
    trackSetupEvent('authentication_activated', 'failure');
    throw error;
  }
}

export async function revertToLocalAuthMethod(tenantId: string, reason: string): Promise<any> {
  try {
    const response = await apiFetch(`/api/tenants/${tenantId}/auth-setup/revert-local`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    trackSetupEvent('authentication_reverted', 'success');
    return reconcileTenant(response);
  } catch (error) {
    trackSetupEvent('authentication_reverted', 'failure');
    throw error;
  }
}
